import { useState, useEffect, useCallback } from 'react';
import { PlusCircle, Building2, Database, CheckCircle2, AlertCircle, Trash2, Edit, UserPlus, Eye, EyeOff } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useToast } from '../../components/ui/toaster.jsx';
import {
  fetchTenants,
  createTenant,
  updateTenant,
  deleteTenant,
  testTenantConnection,
} from './Services/tenantsService.js';
import { createUser } from '../users/Services/usersService.js';

const ESTADO_OPTIONS = [
  { value: 'Activo', label: 'Activo' },
  { value: 'Inactivo', label: 'Inactivo' },
  { value: 'Suspendido', label: 'Suspendido' },
];

const ESTADO_BADGE = {
  Activo: 'success',
  Inactivo: 'secondary',
  Suspendido: 'destructive',
};

const emptyForm = { slug: '', nombre: '', nit: '', estado: 'Activo', mongoUri: '' };

export default function TenantsPage() {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // --- Modal: crear admin de un tenant específico ---
  const [adminDialogTenant, setAdminDialogTenant] = useState(null);
  const [adminForm, setAdminForm] = useState({ nombre: '', correo: '', contrasena: '', contrasena2: '', cedula: '' });
  const [adminErr, setAdminErr] = useState(null);
  const [adminSubmitting, setAdminSubmitting] = useState(false);
  const [adminShowPwd, setAdminShowPwd] = useState(false);

  const openAdminDialog = (t) => {
    setAdminDialogTenant(t);
    setAdminForm({ nombre: '', correo: '', contrasena: '', contrasena2: '', cedula: '' });
    setAdminErr(null);
    setAdminShowPwd(false);
  };
  const closeAdminDialog = () => {
    setAdminDialogTenant(null);
    setAdminErr(null);
  };

  const submitAdmin = async (e) => {
    e.preventDefault();
    setAdminErr(null);
    if (!adminDialogTenant) return;
    if (adminForm.contrasena !== adminForm.contrasena2) {
      setAdminErr('Las contraseñas no coinciden');
      return;
    }
    if (adminForm.contrasena.length < 6) {
      setAdminErr('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setAdminSubmitting(true);
    try {
      const { contrasena2, ...payload } = adminForm;
      payload.rol = 'admin';
      payload.estado = 'Activo';
      payload.tenantId = String(adminDialogTenant._id);
      const r = await createUser(payload);
      if (!r?.success) {
        setAdminErr(r?.message || 'Error al crear el admin');
        return;
      }
      toast.success(
        `Admin creado para ${adminDialogTenant.slug}`,
        `${payload.nombre} (${payload.correo})`,
      );
      closeAdminDialog();
    } catch (e2) {
      setAdminErr(e2?.message || 'Error inesperado');
    } finally {
      setAdminSubmitting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchTenants({ limit: 100, offset: 1, query: {} });
      setData(r?.data || []);
    } catch (e) {
      toast.error('Error al cargar tenants', e?.message || 'Intenta nuevamente');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErr(null);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      slug: t.slug,
      nombre: t.nombre,
      nit: t.nit || '',
      estado: t.estado || 'Activo',
      mongoUri: t.mongoUri || '',
    });
    setErr(null);
    setOpen(true);
  };

  const close = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const payload = { ...form, mongoUri: form.mongoUri?.trim() || undefined };
      const r = editing
        ? await updateTenant(editing._id, payload)
        : await createTenant(payload);
      if (!r?.success) {
        setErr(r?.message || 'Error al guardar');
        return;
      }
      toast.success(editing ? 'Tenant actualizado' : 'Tenant creado');
      close();
      load();
    } catch (e2) {
      setErr(e2?.message || 'Error inesperado');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (t) => {
    if (!window.confirm(`¿Eliminar tenant "${t.nombre}"?`)) return;
    const r = await deleteTenant(t._id);
    if (r?.success) {
      toast.success('Tenant eliminado');
      load();
    } else {
      toast.error('Error al eliminar', r?.message);
    }
  };

  const onTest = async (t) => {
    toast.info(`Probando ${t.slug}…`);
    const r = await testTenantConnection(t._id);
    if (r?.success && r?.code === 200) {
      const info = r.data || {};
      toast.success(
        `${t.slug}: ${info.dbName || ''} · ${info.collections?.length || 0} col · ${info.tookMs}ms`,
      );
    } else {
      toast.error('Conexión fallida', r?.data?.error || r?.message);
    }
    load();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Clientes Operadores (Tenants)</CardTitle>
              <CardDescription>
                Cada tenant corre en su propia base MongoDB. Usa el subdominio
                <code className="text-xs px-1.5 py-0.5 rounded bg-muted mx-1">slug.localhost</code>
                para acceder a su portal.
              </CardDescription>
            </div>
          </div>
          <Button onClick={openCreate}>
            <PlusCircle className="h-4 w-4" /> Nuevo Tenant
          </Button>
        </CardHeader>
        <CardContent>
          {loading && <div className="text-sm text-muted-foreground">Cargando…</div>}
          {!loading && data.length === 0 && (
            <div className="text-sm text-muted-foreground border border-dashed rounded-md p-8 text-center">
              Aún no hay tenants. Crea el primero con <strong>Nuevo Tenant</strong>.
            </div>
          )}
          <div className="space-y-2">
            {data.map((t) => (
              <div
                key={t._id}
                className="border rounded-md p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{t.nombre}</span>
                    <code className="text-xs px-1.5 py-0.5 rounded bg-muted">{t.slug}</code>
                    <Badge variant={ESTADO_BADGE[t.estado] || 'secondary'}>{t.estado}</Badge>
                    <MongoStatusBadge status={t.mongoStatus} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    DB:{' '}
                    <code>
                      {t.mongoUri ? 'URI propia' : t.dbName || `logistic_tenant_${t.slug}`}
                    </code>
                    {t.mongoLastError && (
                      <span className="text-red-600 ml-2">· {t.mongoLastError}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onTest(t)}
                    title="Probar conexión Mongo"
                  >
                    <Database className="h-4 w-4" /> Probar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openAdminDialog(t)}
                    title="Crear usuario administrador para este operador"
                  >
                    <UserPlus className="h-4 w-4" /> Crear admin
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(t)}>
                    <Edit className="h-4 w-4" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDelete(t)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar' : 'Nuevo'} Tenant</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Modifica los datos del operador. El slug no se puede cambiar.'
                : 'Crea un nuevo operador con su propia base de datos.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Slug (subdominio)</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                placeholder="p.ej. demo"
                required
                pattern="[a-z0-9-]+"
                disabled={!!editing}
              />
              <p className="text-xs text-muted-foreground">
                Solo minúsculas, números y guiones. No editable después.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Razón social</Label>
              <Input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>NIT</Label>
              <Input value={form.nit} onChange={(e) => setForm({ ...form, nit: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Estado</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={form.estado}
                onChange={(e) => setForm({ ...form, estado: e.target.value })}
              >
                {ESTADO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>MongoDB URI (opcional)</Label>
              <Input
                value={form.mongoUri}
                onChange={(e) => setForm({ ...form, mongoUri: e.target.value })}
                placeholder="mongodb+srv://user:pass@cluster.mongodb.net/logistic_demo"
              />
              <p className="text-xs text-muted-foreground">
                Vacío = base <code>logistic_tenant_{'{slug}'}</code> sobre el Mongo del entorno. Con URI = cluster dedicado.
              </p>
            </div>
            {err && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                {err}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Guardando…' : editing ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adminDialogTenant} onOpenChange={(o) => !o && closeAdminDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Crear admin del operador
            </DialogTitle>
            <DialogDescription>
              {adminDialogTenant && (
                <>
                  Se creará un usuario con rol <strong>admin</strong> en la base de datos del
                  operador <strong>{adminDialogTenant.nombre}</strong> (
                  <code className="text-xs px-1 py-0.5 rounded bg-muted">{adminDialogTenant.slug}</code>).
                  Podrá iniciar sesión desde <code className="text-xs px-1 py-0.5 rounded bg-muted">{adminDialogTenant.slug}.localhost</code>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdmin} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input
                value={adminForm.nombre}
                onChange={(e) => setAdminForm({ ...adminForm, nombre: e.target.value })}
                placeholder="Nombre completo"
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label>Correo</Label>
              <Input
                type="email"
                value={adminForm.correo}
                onChange={(e) => setAdminForm({ ...adminForm, correo: e.target.value })}
                placeholder="admin@empresa.com"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Contraseña</Label>
              <div className="relative">
                <Input
                  type={adminShowPwd ? 'text' : 'password'}
                  value={adminForm.contrasena}
                  onChange={(e) => setAdminForm({ ...adminForm, contrasena: e.target.value })}
                  required
                  minLength={6}
                  className="pr-12"
                />
                <button
                  type="button"
                  onClick={() => setAdminShowPwd((v) => !v)}
                  className="absolute inset-y-0 right-1.5 my-auto h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition"
                  aria-label={adminShowPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {adminShowPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Repetir contraseña</Label>
              <Input
                type={adminShowPwd ? 'text' : 'password'}
                value={adminForm.contrasena2}
                onChange={(e) => setAdminForm({ ...adminForm, contrasena2: e.target.value })}
                required
                minLength={6}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Cédula / NIT</Label>
              <Input
                value={adminForm.cedula}
                onChange={(e) => setAdminForm({ ...adminForm, cedula: e.target.value })}
                required
              />
            </div>
            {adminErr && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                {adminErr}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeAdminDialog}>
                Cancelar
              </Button>
              <Button type="submit" disabled={adminSubmitting}>
                {adminSubmitting ? 'Creando…' : 'Crear admin'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MongoStatusBadge({ status }) {
  if (!status || status === 'desconocido') {
    return <Badge variant="secondary">mongo: sin probar</Badge>;
  }
  if (status === 'ok') {
    return (
      <Badge variant="success" className="inline-flex items-center gap-1">
        <CheckCircle2 className="h-3 w-3" /> ok
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="inline-flex items-center gap-1">
      <AlertCircle className="h-3 w-3" /> error
    </Badge>
  );
}
